import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from "recharts";
import { CalendarRange } from "lucide-react";

const JAAR_OPTIES = [3, 5, 10];

// Gemiddelde verkoop per kalendermaand over meerdere jaren — het seizoenspatroon
// van één product. Kleur: boven gemiddelde = teal, onder gemiddelde = grijs.
export function Seizoenspatroon({ productId }) {
  const [jaren, setJaren] = useState(5);
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(false);

  useEffect(() => {
    api.get(`/producten/${productId}/seizoenspatroon`, { params: { jaren } })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [productId, jaren]);

  if (!data || !data.maanden?.length) return null;
  const gem = data.gemiddeld_per_maand || 0;
  if (!data.totaal) {
    return (
      <div className="bg-card border border-border rounded-xl p-6" data-testid="seizoenspatroon">
        <h3 className="font-display text-xl font-medium mb-1">Seizoenspatroon</h3>
        <p className="text-sm text-muted-foreground">Nog geen verkopen in de gekozen periode.</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="seizoenspatroon">
      <div className="flex items-start justify-between gap-4 mb-1 flex-wrap">
        <h3 className="font-display text-xl font-medium flex items-center gap-2">
          <CalendarRange className="h-5 w-5 text-primary" /> Seizoenspatroon
        </h3>
        <div className="flex items-center gap-1" data-testid="seizoen-periode">
          {JAAR_OPTIES.map((j) => (
            <Button key={j} size="sm" variant={jaren === j ? "default" : "outline"} className="h-7 px-2 text-xs"
              onClick={() => setJaren(j)} data-testid={`seizoen-jaren-${j}`}>
              {j} jaar
            </Button>
          ))}
          <Button size="sm" variant={detail ? "default" : "outline"} className="h-7 px-2 text-xs"
            onClick={() => setDetail(!detail)} data-testid="seizoen-detail-btn">
            Per jaar
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Gemiddeld verkochte stuks per maand over {data.jaren.join(", ")} — totaal {data.totaal} stuks,
        gemiddeld <span className="text-foreground font-medium">{gem}/maand</span>. Piek in{" "}
        <span className="text-foreground font-medium">{data.piekmaand}</span>, rustigst in{" "}
        <span className="text-foreground font-medium">{data.dalmaand}</span>. Index 100 = gemiddelde maand.
      </p>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data.maanden} margin={{ top: 10, right: 16, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="naam" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
          <Tooltip formatter={(v, n) => [v, n === "gemiddeld" ? "Gemiddeld per maand" : n]}
            labelFormatter={(l) => {
              const m = data.maanden.find((x) => x.naam === l);
              return m ? `${l} — index ${m.index} (${m.jaren_geteld} jaar geteld)` : l;
            }} />
          <ReferenceLine y={gem} stroke="#dc2626" strokeDasharray="5 5"
            label={{ value: "gemiddeld", position: "insideTopRight", fontSize: 10, fill: "#dc2626" }} />
          <Bar dataKey="gemiddeld" name="Gemiddeld per maand" radius={[3, 3, 0, 0]}>
            {data.maanden.map((m) => (
              <Cell key={m.maand} fill={m.gemiddeld >= gem ? "#0f766e" : "#cbd5e1"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {detail && (
        <div className="mt-4 border border-border rounded-lg overflow-x-auto" data-testid="seizoen-tabel">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left p-2">Maand</th>
                {data.jaren.map((j) => <th key={j} className="text-right p-2">{j}</th>)}
                <th className="text-right p-2">Gem.</th>
                <th className="text-right p-2">Index</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.maanden.map((m) => (
                <tr key={m.maand} data-testid={`seizoen-rij-${m.maand}`}>
                  <td className="p-2 capitalize">{m.naam}</td>
                  {data.jaren.map((j) => (
                    <td key={j} className="p-2 text-right tabular-nums text-muted-foreground">
                      {m.per_jaar[String(j)] ?? "—"}
                    </td>
                  ))}
                  <td className="p-2 text-right font-medium tabular-nums">{m.gemiddeld}</td>
                  <td className={`p-2 text-right tabular-nums ${m.index >= 100 ? "text-primary" : "text-muted-foreground"}`}>
                    {m.index}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
